import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  collection,
  addDoc,
  query,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  getDocs,
  where,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { Sparkles, Send, Bot, User, Trash2, Loader, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { db } from "../../firebase";
import { callAiChat } from "../../services/aiService";
import { auth } from "../../firebase";
import { getSubscriptionLimits } from "../../services/subscriptionService";
import Paywall from "../Paywall";
import UsageIndicator from "./UsageIndicator";

const ChatBox = ({ formId, onClose, onQuestionsAdded }) => {
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(null);
  const [usageInfo, setUsageInfo] = useState(null);
  const [pendingQuizData, setPendingQuizData] = useState(null);
  const [quizCreated, setQuizCreated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const pendingRefreshRef = useRef(false);
  // Subscribe to usage counters in Firestore so caps always show and live-update
  useEffect(() => {
    let minuteUnsub = null;
    let dayUnsub = null;
    let intervalId = null;

    const getMinuteKey = (uid) => {
      const now = new Date();
      const bucket = `${now.getUTCFullYear()}-${
        now.getUTCMonth() + 1
      }-${now.getUTCDate()}-${now.getUTCHours()}-${now.getUTCMinutes()}`;
      return `ai_chat:m:${uid}:${bucket}`;
    };
    const getDayKey = (uid) => {
      const now = new Date();
      const bucket = `${now.getUTCFullYear()}-${
        now.getUTCMonth() + 1
      }-${now.getUTCDate()}`;
      return `ai_chat:d:${uid}:${bucket}`;
    };

    const subscribe = () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const minuteId = getMinuteKey(uid);
      const dayId = getDayKey(uid);

      const minuteRef = doc(collection(db, "rate_limits"), minuteId);
      const dayRef = doc(collection(db, "rate_limits"), dayId);

      // Clean up any existing subscriptions first
      if (minuteUnsub) minuteUnsub();
      if (dayUnsub) dayUnsub();

      let latestMinute = 0;
      let latestDay = 0;
      let latestTokens = 0;
      let subscriptionLimits = { aiRequestsPerDay: 3, perMinute: 5 }; // Default Starter limits

      // Fetch subscription limits
      const fetchLimits = async () => {
        if (uid) {
          try {
            const limits = await getSubscriptionLimits(uid);
            subscriptionLimits = {
              aiRequestsPerDay: limits.aiRequestsPerDay,
              perMinute: 5, // Minute limit stays the same for both tiers
            };
            recompute();
          } catch (error) {
            console.error("Error fetching subscription limits:", error);
          }
        }
      };
      fetchLimits();

      const recompute = () => {
        const dailyLimit = subscriptionLimits.aiRequestsPerDay;
        const minuteLimit = subscriptionLimits.perMinute;
        const percentUsed = Math.round((latestDay / dailyLimit) * 100);
        setUsageInfo({
          requestsToday: latestDay,
          requestsThisMinute: latestMinute,
          dailyLimit,
          minuteLimit,
          tokensUsed: latestTokens,
          percentUsed,
        });
      };

      minuteUnsub = onSnapshot(minuteRef, (snap) => {
        latestMinute = snap.exists() ? snap.data()?.count || 0 : 0;
        recompute();
      });
      dayUnsub = onSnapshot(dayRef, (snap) => {
        latestDay = snap.exists() ? snap.data()?.count || 0 : 0;
        latestTokens = snap.exists() ? snap.data()?.totalTokens || 0 : 0;
        recompute();
      });

      // Re-subscribe across minute/day boundaries
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        const newMinuteId = getMinuteKey(uid);
        const newDayId = getDayKey(uid);
        if (
          !minuteRef.id.endsWith(newMinuteId.split(":").slice(-1)[0]) ||
          !dayRef.id.endsWith(newDayId.split(":").slice(-1)[0])
        ) {
          subscribe();
        }
      }, 15000);
    };

    subscribe();

    return () => {
      if (minuteUnsub) minuteUnsub();
      if (dayUnsub) dayUnsub();
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const q = query(collection(db, "chatbot"), where("formId", "==", formId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          isNew:
            lastMessageTimestamp === null
              ? false
              : doc.data().createdAt?.seconds > lastMessageTimestamp,
        }))
        .sort((a, b) => {
          const timeA = a.createdAt?.seconds || 0;
          const timeB = b.createdAt?.seconds || 0;
          return timeA - timeB;
        });

      if (newMessages.length > 0) {
        setLastMessageTimestamp(
          newMessages[newMessages.length - 1].createdAt?.seconds || 0
        );
      }

      setMessages(newMessages);
    });

    return () => unsubscribe();
  }, [formId, lastMessageTimestamp]);

  const clearChat = async () => {
    if (!window.confirm("Are you sure you want to clear all chat messages?"))
      return;

    try {
      setLoading(true);
      const q = query(collection(db, "chatbot"), where("formId", "==", formId));

      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      setMessages([]);
      setLastMessageTimestamp(null);
    } catch (error) {
      console.error("Error clearing chat:", error);
    } finally {
      setLoading(false);
    }
  };

  const TypewriterText = ({ text, isNew }) => {
    const [displayText, setDisplayText] = useState(isNew ? "" : text);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      if (!isNew) {
        setDisplayText(text);
        return;
      }

      if (currentIndex < text.length) {
        const timeout = setTimeout(() => {
          setDisplayText((prev) => prev + text[currentIndex]);
          setCurrentIndex((c) => c + 1);
        }, 15);
        return () => clearTimeout(timeout);
      }
    }, [currentIndex, text, isNew]);

    return (
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown>{displayText}</ReactMarkdown>
      </div>
    );
  };

  // client-side image generation removed; server enriches images

  // Format quiz data for clean display in chat
  const formatQuizForDisplay = (questions) => {
    if (!questions || questions.length === 0) return "";

    let displayText = `🎯 **Quiz Preview - ${questions.length} Question${
      questions.length > 1 ? "s" : ""
    }**\n\n`;

    questions.forEach((question, index) => {
      displayText += `**Question ${index + 1}:**\n`;

      // Find question text component - check multiple possible structures
      const questionText = question.components?.find(
        (comp) => comp.type === "text"
      );
      let questionTextContent = "";

      console.log(
        `🎯 [DEBUG] Question ${index + 1} - Looking for text component:`,
        questionText
      );

      if (questionText) {
        // Try different possible structures for question text
        if (questionText.text?.text) {
          questionTextContent = questionText.text.text;
          console.log(
            `🎯 [DEBUG] Found question text in text.text.text:`,
            questionTextContent
          );
        } else if (questionText.correctAnswer) {
          questionTextContent = questionText.correctAnswer;
          console.log(
            `🎯 [DEBUG] Found question text in correctAnswer:`,
            questionTextContent
          );
        } else if (typeof questionText.text === "string") {
          questionTextContent = questionText.text;
          console.log(
            `🎯 [DEBUG] Found question text in text (string):`,
            questionTextContent
          );
        } else {
          console.log(
            `🎯 [DEBUG] No question text found in text component:`,
            questionText
          );
        }
      } else {
        console.log(
          `🎯 [DEBUG] No text component found for question ${index + 1}`
        );
      }

      // Find answer components and format them
      const answerComponents = question.components?.filter((comp) =>
        [
          "multiple_choice_single",
          "multiple_choice_multi",
          "true_false",
          "short_text_answer",
          "ranking",
          "matching_pairs",
          "single_checkbox",
          "numeric_slider",
          "discrete_slider",
          "image_upload",
          "line",
          "toggle_button",
          "shape",
        ].includes(comp.type)
      );

      if (questionTextContent) {
        displayText += `${questionTextContent}\n`;
      } else {
        // Fallback: check if question text might be in the first option of multiple choice
        const firstMC = answerComponents.find(
          (comp) =>
            comp.type === "multiple_choice_single" ||
            comp.type === "multiple_choice_multi"
        );
        if (firstMC && firstMC.options && firstMC.options.length > 0) {
          // Check if the first option looks like a question (contains question words)
          const firstOption = firstMC.options[0];
          const questionWords = [
            "what",
            "which",
            "who",
            "when",
            "where",
            "why",
            "how",
            "is",
            "are",
            "do",
            "does",
            "can",
            "could",
            "would",
            "should",
          ];
          const isQuestion =
            questionWords.some((word) =>
              firstOption.toLowerCase().includes(word)
            ) || firstOption.includes("?");

          if (isQuestion) {
            displayText += `${firstOption}\n`;
            console.log(
              `🎯 [DEBUG] Found question text in first option:`,
              firstOption
            );
          } else {
            displayText += `*Question text not found*\n`;
          }
        } else {
          displayText += `*Question text not found*\n`;
        }
      }

      console.log(
        `🎯 [DEBUG] Question ${index + 1} - Answer components:`,
        answerComponents
      );

      answerComponents.forEach((comp) => {
        switch (comp.type) {
          case "multiple_choice_single":
            if (comp.options && comp.options.length > 0) {
              comp.options.forEach((option, optIndex) => {
                const isCorrect = optIndex === comp.correctIndex;
                displayText += `${optIndex + 1}. ${option} ${
                  isCorrect ? "✅" : ""
                }\n`;
              });
            }
            break;
          case "multiple_choice_multi":
            if (comp.options && comp.options.length > 0) {
              displayText += `**Options:**\n`;
              comp.options.forEach((option, optIndex) => {
                const isCorrect = comp.correctAnswers?.includes(optIndex);
                displayText += `${optIndex + 1}. ${option} ${
                  isCorrect ? "✅" : ""
                }\n`;
              });
            }
            break;
          case "true_false":
            displayText += `**Answer:** ${
              comp.value ? "True ✅" : "False ✅"
            }\n`;
            break;
          case "short_text_answer":
            if (comp.correctAnswer) {
              displayText += `**Correct Answer:** ${comp.correctAnswer} ✅\n`;
            }
            break;
          case "ranking":
            if (comp.items && comp.items.length > 0) {
              displayText += `**Items to rank:**\n`;
              comp.items.forEach((item, itemIndex) => {
                displayText += `${itemIndex + 1}. ${item}\n`;
              });
              if (comp.correctOrder && comp.correctOrder.length > 0) {
                displayText += `**Correct order:** ${comp.correctOrder
                  .map((i) => comp.items[i])
                  .join(" → ")} ✅\n`;
              }
            }
            break;
          case "matching_pairs":
            if (comp.pairs && comp.pairs.length > 0) {
              displayText += `**Matching pairs:**\n`;
              comp.pairs.forEach((pair, pairIndex) => {
                displayText += `${pairIndex + 1}. ${pair.left} ↔ ${
                  pair.right
                }\n`;
              });
            }
            break;
          case "single_checkbox":
            displayText += `**Checkbox:** ${
              comp.correctValue ? "Checked ✅" : "Unchecked ✅"
            }\n`;
            break;
          case "numeric_slider":
            if (
              comp.minValue !== undefined &&
              comp.maxValue !== undefined &&
              comp.targetValue !== undefined
            ) {
              displayText += `**Range:** ${comp.minValue} to ${comp.maxValue}\n`;
              displayText += `**Target Value:** ${comp.targetValue} ✅\n`;
            }
            break;
          case "discrete_slider":
            if (comp.options && comp.options.length > 0) {
              displayText += `**Options:**\n`;
              comp.options.forEach((option, optIndex) => {
                const isSelected = optIndex === comp.selectedIndex;
                displayText += `${optIndex + 1}. ${option} ${
                  isSelected ? "✅" : ""
                }\n`;
              });
            }
            break;
          case "image_upload":
            if (comp.image) {
              const source = comp.image_metadata?.source || "unknown";
              const sourceIcon =
                source === "unsplash" ? "📷" : source === "dalle" ? "🎨" : "❓";
              displayText += `**Image:** [Visual content included] ${sourceIcon} (${source})\n`;
            } else if (comp.prompt) {
              displayText += `**Image:** ${comp.prompt} 📷\n`;
            }
            break;
          case "line":
            if (
              comp.x1 !== undefined &&
              comp.y1 !== undefined &&
              comp.x2 !== undefined &&
              comp.y2 !== undefined
            ) {
              displayText += `**Line:** From (${comp.x1}, ${comp.y1}) to (${comp.x2}, ${comp.y2})\n`;
            }
            break;
          case "toggle_button":
            displayText += `**Toggle:** ${comp.toggled ? "ON ✅" : "OFF ✅"}\n`;
            break;
          case "shape":
            if (comp.shapeType) {
              displayText += `**Shape:** ${comp.shapeType} 📐\n`;
            }
            break;
          default:
            // Handle other component types if needed
            break;
        }
      });

      displayText += "\n";
    });

    displayText +=
      "💡 *Click 'Create Quiz' below to add these questions to your form.*";

    return displayText;
  };

  // Handle Create Quiz button click
  const handleCreateQuiz = async () => {
    if (!pendingQuizData) return;

    try {
      console.log(
        "🎯 [DEBUG] Starting quiz creation with data:",
        JSON.stringify(pendingQuizData, null, 2)
      );
      const addedCount = await appendQuestionsToForm(pendingQuizData);

      // Clear pending data and show success
      setPendingQuizData(null);
      setQuizCreated(true);

      // Show success message in console (no chat message to avoid LLM calls)
      console.log(`✅ Successfully added ${addedCount} questions to form!`);

      // Hide success message after 3 seconds
      setTimeout(() => setQuizCreated(false), 3000);

      // Trigger form reload if callback exists
      if (onQuestionsAdded) {
        console.log("🎯 [DEBUG] Calling onQuestionsAdded callback");
        onQuestionsAdded();
      }
    } catch (error) {
      console.error("Error creating quiz:", error);
      // Don't add error message to chat either
    }
  };

  const appendQuestionsToForm = async (questionData) => {
    try {
      setGeneratingQuiz(true);
      console.log(
        "🎯 [DEBUG] appendQuestionsToForm called with:",
        questionData
      );

      if (!formId) throw new Error("No form ID provided");

      const formDocRef = doc(db, "forms", formId);
      const formSnapshot = await getDoc(formDocRef);

      if (!formSnapshot.exists()) {
        throw new Error("Form not found. Please save your form first.");
      }

      const questionsCollRef = collection(formDocRef, "questions");
      const questionsSnapshot = await getDocs(questionsCollRef);

      const existingQuestions = questionsSnapshot.docs.map((doc) => ({
        id: parseInt(doc.id),
        ...doc.data(),
      }));

      const sortedQuestions = [...existingQuestions].sort(
        (a, b) => a.order - b.order
      );
      const lastOrder =
        sortedQuestions.length > 0
          ? sortedQuestions[sortedQuestions.length - 1].order
          : -1;
      const lastId =
        sortedQuestions.length > 0
          ? Math.max(...sortedQuestions.map((q) => parseInt(q.id)))
          : 0;

      let addedCount = 0;
      const newQuestionIds = [];

      for (let i = 0; i < questionData.length; i++) {
        // CRITICAL FIX: Deep clone each question to prevent mutations from affecting other questions
        const question = JSON.parse(JSON.stringify(questionData[i]));
        console.log(
          `🎯 [DEBUG] Processing question ${i + 1}:`,
          JSON.stringify(question, null, 2)
        );

        if (!question.components || !Array.isArray(question.components)) {
          console.log(
            `🎯 [DEBUG] Question ${
              i + 1
            } has no components, initializing empty array`
          );
          question.components = [];
        }

        // Log each component for debugging
        question.components.forEach((comp, compIndex) => {
          console.log(
            `🎯 [DEBUG] Question ${i + 1}, Component ${compIndex + 1}:`,
            JSON.stringify(comp, null, 2)
          );
        });

        // Log the text content of this specific question
        const questionTextComp = question.components.find(
          (comp) => comp.type === "text"
        );
        if (questionTextComp) {
          console.log(
            `🎯 [DEBUG] Question ${i + 1} text content:`,
            questionTextComp.text?.text
          );
        } else {
          console.log(`🎯 [DEBUG] Question ${i + 1} has NO text component`);
        }

        // Fix common AI generation issues
        for (const comp of question.components) {
          if (comp.type === "image_upload" && comp.prompt) {
            // image enrichment is handled server-side; drop prompt if any remains
            delete comp.prompt;
          }

          // CRITICAL FIX: Force optionSpacing to be 5px for all multiple choice components
          if (
            (comp.type === "multiple_choice_single" ||
              comp.type === "multiple_choice_multi" ||
              comp.type === "ranking") &&
            comp.optionSpacing !== undefined
          ) {
            console.log(
              `🎯 [DEBUG] Fixing optionSpacing from ${comp.optionSpacing} to 5px for component type: ${comp.type}`
            );
            comp.optionSpacing = 5;
          }

          // Fix: If question text is in correctAnswer field, move it to text.text.text
          if (comp.type === "text" && comp.correctAnswer && !comp.text?.text) {
            console.log(
              `🎯 [DEBUG] Moving question text from correctAnswer to text.text.text:`,
              comp.correctAnswer
            );
            comp.text = {
              text: comp.correctAnswer,
              format: { bold: true, size: 18 },
            };
            delete comp.correctAnswer;
          }
        }

        // CRITICAL FIX: Only apply text extraction logic if there's actually no text component
        // This prevents copying the same question text to all questions
        const textComponent = question.components.find(
          (comp) => comp.type === "text"
        );

        // Only try to extract text from options if there's NO text component at all
        if (!textComponent) {
          console.log(
            `🎯 [DEBUG] Question ${
              i + 1
            } has no text component, checking if text is in options`
          );

          const mcComponent = question.components.find(
            (comp) =>
              (comp.type === "multiple_choice_single" ||
                comp.type === "multiple_choice_multi") &&
              comp.options &&
              comp.options.length > 0
          );

          if (mcComponent) {
            const firstOption = mcComponent.options[0];
            const questionWords = [
              "what",
              "which",
              "who",
              "when",
              "where",
              "why",
              "how",
              "is",
              "are",
              "do",
              "does",
              "can",
              "could",
              "would",
              "should",
            ];
            const isQuestion =
              questionWords.some((word) =>
                firstOption.toLowerCase().includes(word)
              ) || firstOption.includes("?");

            if (isQuestion) {
              console.log(
                `🎯 [DEBUG] Question ${
                  i + 1
                }: Moving question text from first option to text component:`,
                firstOption
              );
              // Create a text component for the question
              const questionTextComponent = {
                id: Math.max(...question.components.map((c) => c.id || 0)) + 1,
                type: "text",
                position: { top: 60, left: 100 },
                width: 600,
                height: 80,
                opacity: 1,
                text: {
                  text: firstOption,
                  format: { bold: true, size: 18 },
                },
              };

              // Remove the question from the first option
              mcComponent.options = mcComponent.options.slice(1);

              // Add the text component at the beginning
              question.components.unshift(questionTextComponent);
            }
          }
        } else {
          console.log(
            `🎯 [DEBUG] Question ${i + 1} already has text component:`,
            textComponent.text?.text
          );
        }

        const newId = lastId + i + 1;
        const newOrder = lastOrder + i + 1;

        question.components.forEach((comp, idx) => {
          if (!comp.position) {
            comp.position = { top: 100 + idx * 50, left: 100 + idx * 20 };
          }
          if (!comp.opacity && comp.opacity !== 0) {
            comp.opacity = 1;
          }
        });

        const questionDocRef = doc(
          collection(formDocRef, "questions"),
          newId.toString()
        );

        // Deep clone the question object to ensure components array is not shared
        const questionToSave = JSON.parse(
          JSON.stringify({
            id: newId,
            backgroundColor: question.backgroundColor || "#FFFFFF",
            points: question.points || 0,
            maxAttempts: question.maxAttempts || 0,
            order: newOrder,
            components: question.components || [],
          })
        );

        console.log(
          `🎯 [DEBUG] Saving question ${newId}:`,
          JSON.stringify(questionToSave, null, 2)
        );

        await setDoc(questionDocRef, questionToSave);

        newQuestionIds.push(newId);
        addedCount++;
      }

      console.log(
        `Successfully added ${addedCount} questions with IDs: ${newQuestionIds.join(
          ", "
        )}`
      );

      // Trigger success overlay and reload
      if (onQuestionsAdded && typeof onQuestionsAdded === "function") {
        onQuestionsAdded(addedCount, newQuestionIds);
        // Delay reload to allow success overlay to display
        setTimeout(() => {
          window.location.reload();
        }, 2500); // Matches SuccessOverlay duration
      }

      return addedCount;
    } catch (error) {
      console.error("Error appending questions:", error);
      throw error;
    } finally {
      setGeneratingQuiz(false);
    }
  };

  const getChatbotResponse = async (userMessage) => {
    try {
      // AI now intelligently decides the action (no client-side mode guessing)
      const payload = {
        sessionId: `form_${formId}`,
        messages: [{ role: "user", content: userMessage }],
        mode: "auto", // AI decides: CREATE/UPDATE/DELETE/EDIT/CHAT
        formId,
        imagePolicy: "auto",
        requestId: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Add request ID for deduplication
      };
      const data = await callAiChat(payload);
      console.log(
        "🎯 [DEBUG] Full AI response:",
        JSON.stringify(data, null, 2)
      );

      // Update usage info from response
      if (data?.usageInfo) {
        setUsageInfo(data.usageInfo);
      }

      let responseContent = data?.message?.content || "";
      const action = data?.action || "CHAT";

      // Handle different action types
      if (
        action === "CREATE" &&
        Array.isArray(data?.questions) &&
        data.questions.length > 0
      ) {
        console.log(
          "🎯 [DEBUG] AI returned CREATE action with questions:",
          JSON.stringify(data.questions, null, 2)
        );

        // Debug: Check if backgroundColor is present in AI response
        data.questions.forEach((q, index) => {
          console.log(
            `🎯 [DEBUG] Question ${index + 1} backgroundColor:`,
            q.backgroundColor
          );
          console.log(
            `🎯 [DEBUG] Question ${index + 1} full data:`,
            JSON.stringify(q, null, 2)
          );
        });

        // CRITICAL FIX: Deep clone the questions array before storing to prevent mutation issues
        const clonedQuestions = JSON.parse(JSON.stringify(data.questions));

        // Store the cloned quiz data for the Create Quiz button
        setPendingQuizData(clonedQuestions);

        // Return ONLY clean formatted preview using cloned data (no raw AI response)
        const quizPreview = formatQuizForDisplay(clonedQuestions);
        console.log("🎯 [DEBUG] Formatted quiz preview:", quizPreview);
        return quizPreview;
      } else if (action === "DELETE") {
        pendingRefreshRef.current = true;
        return `✅ ${responseContent}`;
      } else if (action === "UPDATE") {
        pendingRefreshRef.current = true;
        return `✅ ${responseContent}`;
      } else if (action === "EDIT") {
        pendingRefreshRef.current = true;
        return `✅ ${responseContent}`;
      }

      return responseContent || "Sorry, I didn't understand that.";
    } catch (error) {
      console.error("AI callable error:", error);

      // Check for rate limit error
      if (error?.message?.includes("Rate limit exceeded")) {
        setShowPaywall(true);
        return "⚠️ " + error.message;
      }

      return "Error: Unable to get a response. " + (error?.message || "");
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (inputValue.trim() === "" || isProcessing) return;

    // Check if user has reached daily limit
    if (usageInfo && usageInfo.requestsToday >= usageInfo.dailyLimit) {
      setShowPaywall(true);
      return;
    }

    try {
      setIsProcessing(true);

      await addDoc(collection(db, "chatbot"), {
        sender: "user",
        message: inputValue,
        createdAt: serverTimestamp(),
        formId: formId,
      });

      const userMessage = inputValue;
      setInputValue("");
      // Clear any pending quiz data when user sends new message
      setPendingQuizData(null);
      setGeneratingQuiz(true);

      const chatbotResponse = await getChatbotResponse(userMessage);
      await addDoc(collection(db, "chatbot"), {
        sender: "chatbot",
        message: chatbotResponse,
        createdAt: serverTimestamp(),
        formId: formId,
      });

      // If an UPDATE/DELETE/EDIT action occurred, refresh to show latest state
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
      setGeneratingQuiz(false);
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Sparkles className="text-white" size={24} />
          <h1 className="text-xl font-semibold text-white">Quiz Designer AI</h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={clearChat}
            disabled={loading || generatingQuiz}
            className="p-2 text-white hover:text-red-200 transition-colors"
            title="Clear chat history"
          >
            <Trash2 size={20} />
          </button>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors text-xl font-bold"
          >
            ×
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Usage Indicator - always render and stick to top */}
        <div className="mb-3 sticky -top-3">
          <UsageIndicator usageInfo={usageInfo} />
        </div>

        {messages.length === 0 && (
          <div className="text-center p-6 text-gray-500">
            <Bot size={40} className="mx-auto mb-3 text-blue-500 opacity-70" />
            <h3 className="font-semibold text-gray-700 mb-1">
              Quiz Designer AI
            </h3>
            <p className="text-sm mb-3">
              Hello! I can help you add questions or images to your current
              form.
            </p>
            <div className="grid grid-cols-1 gap-2 max-w-sm mx-auto text-sm">
              <button
                onClick={() =>
                  setInputValue(
                    "Create 3 multiple choice questions about solar system planets in a playful style"
                  )
                }
                className="p-2 text-left rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                Create 3 multiple choice questions about solar system planets in
                a playful style
              </button>
              <button
                onClick={() =>
                  setInputValue(
                    "Create a corporate-style quiz about business ethics"
                  )
                }
                className="p-2 text-left rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                Create a corporate-style quiz about business ethics
              </button>
              <button
                onClick={() =>
                  setInputValue(
                    "Make the current quiz use a minimalist design with darker colors"
                  )
                }
                className="p-2 text-left rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                Make the current quiz use a minimalist design with darker colors
              </button>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex items-start space-x-2 ${
              msg.sender === "user" ? "flex-row-reverse space-x-reverse" : ""
            }`}
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                msg.sender === "user" ? "bg-blue-100" : "bg-emerald-100"
              }`}
            >
              {msg.sender === "user" ? (
                <User size={20} className="text-blue-600" />
              ) : (
                <Bot size={20} className="text-emerald-600" />
              )}
            </div>
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                msg.sender === "user"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {msg.sender === "chatbot" ? (
                <div>
                  <TypewriterText text={msg.message} isNew={msg.isNew} />
                  {/* Create Quiz Button - Only show for the last message with pending quiz data */}
                  {pendingQuizData &&
                    msg.id === messages[messages.length - 1]?.id && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <button
                          onClick={handleCreateQuiz}
                          disabled={generatingQuiz}
                          className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-1.5"
                        >
                          <Sparkles size={14} />
                          Create Quiz ({pendingQuizData.length})
                        </button>
                      </div>
                    )}
                </div>
              ) : (
                msg.message
              )}
            </div>
          </div>
        ))}

        {generatingQuiz && (
          <div className="flex items-start space-x-2 mb-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Bot size={20} className="text-emerald-600" />
            </div>
            <div className="bg-gray-100 rounded-2xl px-4 py-3 max-w-[75%]">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Loader size={18} className="text-blue-500 animate-spin" />
                    <div
                      className="absolute inset-0 animate-ping opacity-30 rounded-full bg-blue-400"
                      style={{ animationDuration: "3s" }}
                    ></div>
                  </div>
                  <div className="text-gray-700">
                    <p className="font-medium">Creating content...</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Designing components for your form
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && !generatingQuiz && (
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Bot size={20} className="text-emerald-600" />
            </div>
            <div className="bg-gray-100 rounded-2xl px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleUserSubmit}
        className="border-t border-gray-100 p-4"
      >
        <div className="space-y-2">
          {/* Success Message */}
          {quizCreated && (
            <div className="flex justify-center">
              <div className="px-4 py-1.5 bg-green-100 text-green-800 text-sm rounded-md border border-green-200 flex items-center gap-1.5">
                <Check size={14} />
                Quiz created successfully!
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <input
              type="text"
              className="flex-1 px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ask me to add, update, or delete questions..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={
                loading ||
                generatingQuiz ||
                isProcessing ||
                (usageInfo && usageInfo.requestsToday >= usageInfo.dailyLimit)
              }
            />
            <button
              type="submit"
              disabled={
                loading ||
                generatingQuiz ||
                isProcessing ||
                (usageInfo &&
                  usageInfo.requestsToday >= usageInfo.dailyLimit) ||
                inputValue
                  .trim()
                  .split(/\s+/)
                  .filter((w) => w.length > 0).length > 500
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                usageInfo && usageInfo.requestsToday >= usageInfo.dailyLimit
                  ? "Daily limit reached"
                  : inputValue
                      .trim()
                      .split(/\s+/)
                      .filter((w) => w.length > 0).length > 500
                  ? "Message too long (max 500 words)"
                  : "Send message"
              }
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </form>

      {/* Paywall Modal */}
      <Paywall
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        featureName="more AI requests"
      />
    </div>
  );
};

export default ChatBox;
